// The Leads "Filter by" panel, extracted so every module renders the exact same
// thing instead of each keeping its own copy: categories down the left with a
// tick on the ones holding selections, multi-select checkboxes on the right,
// Clear / Apply in the footer.
//
// Leads.module.css is imported rather than duplicated — that keeps the styling
// and spacing identical to Leads by construction. LeadsFilterPanel.module.css
// layers on top of it for panel positioning and small-screen behaviour.
//
// Behaviour matches Leads exactly:
//   • ticking a box changes nothing until Apply is pressed
//   • pending ticks survive closing the panel without applying
//   • the "None" category drops every pending selection
import React, { useMemo, useState } from "react";
import {
  ClickAwayListener,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import cssClasses from "../Components/Leads/Leads.module.css";
import own from "./LeadsFilterPanel.module.css";
import Button from "./Button";
import SearchBar from "./Search";
import checked from "../Assets/Images/Common/checked.svg";

/* Typography override Leads applies to every filter checkbox label. */
const CHECKBOX_LABEL_SX = [
  {
    "& .MuiTypography-root": {
      fontFamily: "Inter-Medium !important",
      fontStyle: "normal",
      fontWeight: "400",
      fontSize: "1.6rem !important",
      lineHeight: "2.4rem !important",
      /* identical to box height, or 150% */

      /* Gray/900 */

      color: "#101828",
    },
  },
];

/** Right-hand checkbox column. */
const FilterCheckboxList = ({
  options = [],
  selectedIds = [],
  onToggle,
  emptyMessage = "No Options Found",
}) => {
  if (options.length === 0) {
    return <div className={cssClasses.label}>{emptyMessage}</div>;
  }

  return (
    <>
      {options.map((each) => (
        <FormGroup key={each.id}>
          <FormControlLabel
            sx={CHECKBOX_LABEL_SX}
            checked={selectedIds.includes(each.id)}
            onChange={() => onToggle(each.id)}
            control={
              <Checkbox
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: 28,
                  },
                }}
              />
            }
            label={each.label}
          />
        </FormGroup>
      ))}
    </>
  );
};

/**
 * @param categories      [{ label, options:[{id,label}], selectedIds:[], onToggle(id),
 *                           emptyMessage, searchable, searchPlaceholder }]
 *                        searchable adds Leads' search box above the checkbox
 *                        list and filters the options by label as you type.
 * @param onClearPending  drop pending selections only — the "None" category, as in Leads
 * @param onClear         drop pending *and* applied filters — the Clear button
 * @param onApply         commit pending selections to the applied filters
 * @param title           label above the trigger, defaults to "Filter by"
 * @param align           "left" (default) or "right" — which edge the panel is
 *                        anchored to. Use "right" when the trigger sits on the
 *                        right of its row, or the panel runs off screen.
 * @param panelWidth      CSS width for the panel on desktop. Small screens
 *                        always override this with the available width.
 */
export default function LeadsFilterPanel({
  categories = [],
  onClearPending,
  onClear,
  onApply,
  title = "Filter by",
  align = "left",
  panelWidth = "46rem",
}) {
  const [showDropDown, setShowDropDown] = useState(false);
  // Which category's checkbox column is on screen (label | null)
  const [activeCategory, setActiveCategory] = useState(null);
  // Query for the active category's search box (only when searchable).
  const [query, setQuery] = useState("");

  /* Categories carrying selections get the tick and make up the trigger label. */
  const selectedListDropDown = useMemo(
    () => categories.filter((c) => (c.selectedIds || []).length > 0).map((c) => c.label),
    [categories]
  );

  const listOfDropDown = useMemo(
    () => [
      { label: "None", select: selectedListDropDown.length === 0 },
      ...categories.map((c) => ({
        label: c.label,
        select: selectedListDropDown.includes(c.label),
      })),
    ],
    [categories, selectedListDropDown]
  );

  const active = categories.find((c) => c.label === activeCategory);

  // Options actually listed — narrowed by the search box when the category is
  // searchable. Matching is a case-insensitive substring on the label.
  const visibleOptions = useMemo(() => {
    const options = active?.options || [];
    if (!active?.searchable) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.label ?? "").toLowerCase().includes(q));
  }, [active, query]);

  const handleCategoryClick = (label) => {
    if (label === "None") {
      // Leads' "None" drops the pending ticks but leaves the applied filter be.
      onClearPending?.();
      setActiveCategory(null);
      setQuery("");
      return;
    }
    setActiveCategory(label);
    setQuery("");
  };

  const closePanel = () => {
    setActiveCategory(null);
    setQuery("");
    setShowDropDown(false);
  };

  return (
    <div className={cssClasses.wrapperFilter}>
      <div className={cssClasses.filterTitle}>{title}</div>
      <div className={`${cssClasses.wrapperDropDown} ${own.dropdownWrap}`}>
        <div
          className={cssClasses.wrapperCustomiseDropDown}
          onClick={(e) => {
            e.stopPropagation();
            setShowDropDown(!showDropDown);
          }}
        >
          <div className={cssClasses.dropDownLabel}>
            <span className={cssClasses.dropDownLabelValue}>
              {selectedListDropDown.join()}
            </span>
            {selectedListDropDown.length === 0 && (
              <span className={cssClasses.dropDownLabelValue}>None</span>
            )}
          </div>
          {showDropDown && <KeyboardArrowUpIcon className={cssClasses.dropDownIcon} />}
          {showDropDown === false && (
            <KeyboardArrowDownIcon className={cssClasses.dropDownIcon} />
          )}
        </div>
        {showDropDown && (
          <ClickAwayListener onClickAway={() => setShowDropDown(false)}>
            <div
              className={[
                cssClasses.containerCustomiseDropDown,
                own.panel,
                align === "right" ? own.panelRight : own.panelLeft,
              ].join(" ")}
              style={{ width: panelWidth, maxWidth: "calc(100vw - 3.2rem)" }}
            >
              <div className={`${cssClasses.wrapperSelectAndCheckbox} ${own.stack}`}>
                <div className={`${cssClasses.wrapperEachSelect} ${own.categories}`}>
                  {listOfDropDown.map((each, index) => (
                    <div
                      key={index}
                      className={
                        each.select ? cssClasses.selectedVal : cssClasses.containerEachSelect
                      }
                    >
                      <div
                        className={cssClasses.eachSelect}
                        onClick={() => handleCategoryClick(each.label)}
                      >
                        {each.label}
                      </div>
                      {each.select && (
                        <img src={checked} alt={"checked"} style={{ marginRight: "2rem" }} />
                      )}
                    </div>
                  ))}
                </div>
                <div className={`${cssClasses.wrapperCheckbox1} ${own.optionsWrap}`}>
                  {active?.searchable && (
                    <div style={{ width: "100%", marginBottom: "1.6rem" }}>
                      <SearchBar
                        id={`FilterPanelSearch-${active.label}`}
                        placeholder={active.searchPlaceholder || "Search"}
                        search={(e) => setQuery(e.target.value)}
                        width="100%"
                      />
                    </div>
                  )}
                  <div className={`${cssClasses.wrapperCheckbox} ${own.optionsScroll}`}>
                    {active && (
                      <FilterCheckboxList
                        options={visibleOptions}
                        selectedIds={active.selectedIds}
                        onToggle={active.onToggle}
                        emptyMessage={
                          (active.options || []).length === 0
                            ? active.emptyMessage
                            : "No matches found"
                        }
                      />
                    )}
                  </div>
                  <div className={cssClasses.wrapperDropDownButtons}>
                    <div>
                      <Button
                        style={{
                          color: "#344054",
                          backgroundColor: "#ffffff",
                          fontSize: "1.4rem",
                          lineHeight: "2rem",
                        }}
                        padding={"0.8rem"}
                        btnName={"Clear"}
                        clicked={() => {
                          onClear?.();
                          closePanel();
                        }}
                      />
                    </div>
                    <div>
                      <Button
                        style={{
                          color: "#ffffff",
                          backgroundColor: "#1570EF",
                          fontSize: "1.4rem",
                          lineHeight: "2rem",
                        }}
                        padding={"0.8rem"}
                        btnName={"Apply"}
                        clicked={() => {
                          onApply?.();
                          closePanel();
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ClickAwayListener>
        )}
      </div>
    </div>
  );
}
